import React, { useCallback, useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

const slides = [
  { number: "01", title: "Structured Cabling", subtitle: "High-density copper and fiber infrastructure for enterprise environments.", image: "/img/portfolio/03-small.png" },
  { number: "02", title: "Wi-Fi Deployments", subtitle: "RF-validated access point placement for consistent, high-capacity coverage.", image: "/img/portfolio/04-small.png" },
  { number: "03", title: "Network Rack Builds", subtitle: "Clean, labeled, and documented rack installations built to industry standards.", image: "/img/portfolio/05-small.png" },
  { number: "04", title: "Site Surveys", subtitle: "On-site assessment and heatmap analysis before every deployment.", image: "/img/portfolio/06-small.png" },
  { number: "05", title: "Edge & Security Infrastructure", subtitle: "Fortinet and Cisco edge deployments with SD-WAN and firewall policy.", image: "/img/portfolio/07-small.png" },
  { number: "06", title: "Campus Networking", subtitle: "End-to-end campus network design and deployment for educational institutions.", image: "/img/portfolio/08-small.png" },
  { number: "07", title: "Data Center Cabling", subtitle: "Precision fiber and copper runs for high-density data center environments.", image: "/img/portfolio/09-small.png" },
  { number: "08", title: "Access Control", subtitle: "IP-based access control systems integrated with existing network infrastructure.", image: "/img/portfolio/10-small.png" },
  { number: "09", title: "Surveillance Systems", subtitle: "IP camera deployments with centralized management and secure storage.", image: "/img/portfolio/11-small.png" },
  { number: "10", title: "Wireless Surveys", subtitle: "Predictive and post-install RF analysis ensuring zero dead zones.", image: "/img/portfolio/12-small.png" },
  { number: "11", title: "Government Deployments", subtitle: "Secure infrastructure for public sector facilities with compliance in mind.", image: "/img/portfolio/13-small.png" },
  { number: "12", title: "VoIP & Unified Comms", subtitle: "Voice over IP solutions with QoS-optimized network configurations.", image: "/img/portfolio/14-small.png" },
  { number: "13", title: "Fiber Backbone", subtitle: "Multi-mode and single-mode fiber backbone engineering across campuses.", image: "/img/portfolio/15-small.png" },
  { number: "14", title: "Smart Hands Support", subtitle: "On-site technical support and device installation for remote teams.", image: "/img/portfolio/16-small.png" },
  { number: "15", title: "Industrial Networking", subtitle: "Ruggedized network infrastructure for manufacturing and industrial sites.", image: "/img/portfolio/17-small.png" },
];

export const ProjectShowcase = () => {
  const autoplayRef = useRef(
    Autoplay({ delay: 4800, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center", skipSnaps: false, dragFree: false },
    [autoplayRef.current]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onInit = useCallback((api) => {
    setScrollSnaps(api.scrollSnapList());
  }, []);

  const onSelect = useCallback((api) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  return (
    <section id="showcase">
      {/* Section Header */}
      <div className="container">
        <div className="section-header text-center">
          <div className="eyebrow">Project Showcase</div>
          <h2>Built for real-world performance</h2>
          <p className="section-sub">
            A curated snapshot of our network deployments, field execution, and infrastructure quality.
          </p>
        </div>
      </div>

      {/* Embla Viewport */}
      <div className="showcase-viewport" ref={emblaRef}>
        <div className="showcase-track">
          {slides.map((slide, i) => (
            <div className="showcase-slide" key={i}>
              <div className={`showcase-card${i === selectedIndex ? " showcase-card--active" : ""}`}>
                {/* Image */}
                <div className="showcase-img-wrap">
                  <img src={slide.image} alt={slide.title} className="showcase-img" />
                </div>
                {/* Content */}
                <div className="showcase-content">
                  <span className="showcase-num">{slide.number}</span>
                  <div className="showcase-text">
                    <h3 className="showcase-title">{slide.title}</h3>
                    <p className="showcase-subtitle">{slide.subtitle}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="showcase-controls">
        {/* Prev/Next — desktop only */}
        <button className="showcase-arrow showcase-arrow--prev" onClick={scrollPrev} aria-label="Previous">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Dots */}
        <div className="showcase-dots">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              className={`showcase-dot${i === selectedIndex ? " showcase-dot--active" : ""}`}
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        <button className="showcase-arrow showcase-arrow--next" onClick={scrollNext} aria-label="Next">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </section>
  );
};
